import { Ng2StateDeclaration } from '@uirouter/angular';
import { Rejection, StateService, Transition } from '@uirouter/core';

import { Analytics, CimaCore, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { BREADCRUMB_TOKEN, CONFIG_TOKEN, ParamType } from 'core/constants';
import { AnalyticsErrorMessageTemplates } from 'my-account/constants';
import { HttpError } from 'services/http/error';

import { Xm5GPlanOverview } from 'my-account/settings/level-move/plan-overview/base';
import { XmLevelMoveEligibility } from 'my-account/settings/level-move/eligibility/eligibility';
import { Cms5GPlanOverview } from 'store/cms/models/plan-overview';
import { XmLevelMoveInProgress } from './in-progress/in-progress';
import { XmLevelMoveSuccess } from 'my-account/settings/level-move/success/success';
import { XmLevelMovePending } from 'my-account/settings/level-move/pending/pending';
import { XmLevelMoveStart } from './start/level-move-start';
import { XmLevelMoveError } from './shared/level-move-error/level-move-error';
import {
    CmsAccountDataPlanCollection,
    CmsLevelMoveEligibility,
    CmsLevelMoveError,
    CmsLevelMoveInProgress,
    CmsLevelMovePending,
    CmsLevelMoveStart,
    CmsLevelMoveSuccess
} from 'store/cms/models';
import { CustomerGuard, LevelMoveLandingGuard, MemberViewerGuard } from 'src/xm/route-guards';
import { AccountSummary } from 'store/account-summary/models';
import { DiscountPromoCollection } from 'store/promotions/discount-promotion/models';
import { LevelMoveEligibility } from 'store/level-move/models';
import { IXMOptions } from 'core/interfaces';
import { Account, User } from 'store/user/models';
import { XmLevelMoveReviewOrder } from './review-order/level-move-review-order';

export function GetCms5GPlanOverViewData(xmStore: XmStore): Promise<Cms5GPlanOverview> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<Cms5GPlanOverview>(StoreAction.GET_5G_PLAN_OVERVIEW, Cms5GPlanOverview));
}

export function GetPlanOverviewBreadcrumb(cmsData: Cms5GPlanOverview): AccountBreadcrumb {
    return {
        label: cmsData.cbOverview.header,
        stateName: 'account.settings.level-move.landing'
    };
}

export function GetCmsLevelMoveEligibility(xmStore: XmStore): Promise<CmsLevelMoveEligibility> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMoveEligibility>(StoreAction.GET_LEVEL_MOVE_ELIGIBILITY_CMS, CmsLevelMoveEligibility));
}

export function GetCmsLevelMoveStart(xmStore: XmStore): Promise<CmsLevelMoveStart> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMoveStart>(StoreAction.GET_LEVEL_MOVE_START, CmsLevelMoveStart));
}

export function GetCmsLevelMoveInProgress(xmStore: XmStore): Promise<CmsLevelMoveInProgress> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMoveInProgress>(StoreAction.GET_LEVEL_MOVE_IN_PROGRESS_CMS, CmsLevelMoveInProgress));
}

export function GetCmsLevelMoveSuccess(xmStore: XmStore): Promise<CmsLevelMoveSuccess> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMoveSuccess>(StoreAction.GET_LEVEL_MOVE_SUCCESS_CMS, CmsLevelMoveSuccess));
}

export function GetCmsLevelMovePending(xmStore: XmStore): Promise<CmsLevelMovePending> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMovePending>(StoreAction.GET_LEVEL_MOVE_PENDING_CMS, CmsLevelMovePending));
}

export function GetCmsLevelMoveError(xmStore: XmStore): Promise<CmsLevelMoveError> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsLevelMoveError>(StoreAction.GET_LEVEL_MOVE_ERROR_CMS, CmsLevelMoveError));
}

export function GetAccountDataPlanPageData(xmStore: XmStore): Promise<CmsAccountDataPlanCollection> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<CmsAccountDataPlanCollection>(StoreAction.GET_ACCOUNT_DATA_PLAN, CmsAccountDataPlanCollection));
}

export function CheckLevelMoveRoutes(transition: Transition): Promise<boolean> {

    if (!transition.to().name.includes('level-move') || transition.to().name === 'account.settings.level-move.landing') {
        return Promise.resolve(true);
    }

    const state: StateService = transition.router.stateService;

    transition.abort();
    state.go('account.settings.level-move.landing');

    return Promise.reject(Rejection.aborted('Level Move route guard'));
}

export function GetLevelMoveBreadcrumb(cmsData: CmsLevelMoveEligibility): AccountBreadcrumb {
    return {
        label: cmsData.planSevenPageTitle,
        stateName: 'account.settings.level-move.eligibility'
    };
}

export function GetLevelMoveSuccessBreadcrumb(cmsData: CmsLevelMoveSuccess, transition: Transition): AccountBreadcrumb {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const cimaCore: CimaCore = transition.injector().get(CimaCore);

    return {
        label: (config.ENABLE_BILL_PREVIEW && !cimaCore.isBusinessUser) || (config.ENABLE_CBM_BILL_PREVIEW && cimaCore.isBusinessUser) ? cmsData.billPreviewSuccess.pageTitle : cmsData.success.pageTitle,
        stateName: 'account.settings.level-move.success'
    };
}

export function GetLevelMovePendingBreadcrumb(cmsData: CmsLevelMovePending): AccountBreadcrumb {
    return {
        label: cmsData.pageTitle,
        stateName: 'account.settings.level-move.pending'
    };
}

export function GetLevelMoveErrorBreadcrumb(): AccountBreadcrumb {
    return {
        label: 'Plan Switch',
        stateName: 'account.settings.level-move.error'
    };
}

export function GetLevelMoveReviewOrderBreadcrumb(): AccountBreadcrumb {
    return {
        label: 'Order Summary',
        stateName: 'account.settings.level-move.start.review-order'
    };
}

export function AddErrorMessageToDigitalData(analytics: Analytics, transition: Transition): Promise<void> {
    analytics.updateErrorMsg(AnalyticsErrorMessageTemplates.LEVEL_MOVE_API_ERROR.replace('[ERRORCODE]', transition.params().errorCode || 'N/A'));

    return Promise.resolve();
}

export function AddIneligibleMessageToDigitalData(analytics: Analytics, transition: Transition): Promise<void> {
    analytics.updateErrorMsg(AnalyticsErrorMessageTemplates.LEVEL_MOVE_ELIGIBILITY_ERRORS.replace('[ERRORCODE]', transition.params().errorCode || 'N/A'));

    return Promise.resolve();
}

export function GetAccountSummary(xmStore: XmStore, account: Account, transition: Transition): Promise<AccountSummary|void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const cimaCore: CimaCore = transition.injector().get(CimaCore);
    
    let action: StoreAction;
    if (config.ENABLE_CBM_GEMINI && cimaCore.isBusinessUser && account.isNoLevel) {
        action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
    } else if (account.isSegmentSix && !cimaCore.isBusinessUser) {
        action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
    } else if (account.isSegmentFive || account.isSegmentSix) {
        action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
    } else {
        action = StoreAction.GET_ACCOUNT_SUMMARY;
    }

    return XmStoreUtil.defaultCatch(xmStore.findPromise<AccountSummary>(action, AccountSummary));
}

export function GetDiscountPromotions(xmStore: XmStore): Promise<DiscountPromoCollection|void> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection));
}

export function GetLevelMoveEligibility(xmStore: XmStore): Promise<LevelMoveEligibility|void> {
    return XmStoreUtil.defaultCatch(xmStore.findPromise<LevelMoveEligibility>(StoreAction.GET_LEVEL_MOVE_ELIGIBILITY, LevelMoveEligibility));
}

export function GetAccountInfo(xmStore: XmStore): Promise<Account | HttpError> {
    return XmStoreUtil.defaultCatch(xmStore.findChildPromise<Account, User>(StoreAction.GET_ACCOUNT_INFO, 'account',
        Account, User).catch((error: HttpError) => Promise.resolve(error)));
}

export const LEVEL_MOVE_ROUTES: Ng2StateDeclaration[] = [
    {
        abstract: true,
        name: 'account.settings.level-move',
        url: '/level-move',
        resolve: [
            {
                token: 'checkLevelMoveRoutes',
                deps: [ Transition ],
                resolveFn: CheckLevelMoveRoutes
            }
        ]
    },
    {
        name: 'account.settings.level-move.landing',
        url: '',
        data: {
            screenName: 'level-move-landing',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'accountInfo',
                deps: [ XmStore ],
                resolveFn: GetAccountInfo
            },
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'memberViewerCheck',
                deps: ['accountInfo', CimaCore, StateService, Transition],
                resolveFn: MemberViewerGuard
            },
            {
                token: 'levelMoveLandingGuard',
                deps: ['accountInfo', CimaCore, StateService, Transition],
                resolveFn: LevelMoveLandingGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCms5GPlanOverViewData
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData' ],
                resolveFn: GetPlanOverviewBreadcrumb
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, 'accountInfo', Transition ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'levelMoveEligibility',
                deps: [ XmStore ],
                resolveFn: GetLevelMoveEligibility
            }
        ],
        views: {
            'content@account': {
                component: Xm5GPlanOverview
            }
        }
    },
    {
        name: 'account.settings.level-move.eligibility',
        url: '/eligibility',
        data: {
            screenName: 'level-move-eligibility',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveEligibility
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData' ],
                resolveFn: GetLevelMoveBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveEligibility
            }
        }
    },
    {
        name: 'account.settings.level-move.start',
        url: '/start',
        data: {
            screenName: 'level-move-start',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveStart
            },
            {
                token: 'accountDataPlanPageData',
                deps: [ XmStore ],
                resolveFn: GetAccountDataPlanPageData
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData' ],
                resolveFn: GetLevelMoveBreadcrumb
            },
            {
                token: 'accountSummary',
                deps: [ XmStore, 'accountInfo', Transition ],
                resolveFn: GetAccountSummary
            },
            {
                token: 'discountPromotion',
                deps: [ XmStore ],
                resolveFn: GetDiscountPromotions
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveStart
            }
        }
    },
    {
        name: 'account.settings.level-move.start.review-order',
        url: '/review-order',
        data: {
            screenName: 'level-move-review-order',
            subCategory1: 'my-account'
        },
        params: {
            planOverrides: {
                type: ParamType.any,
                raw: true
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetLevelMoveReviewOrderBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveReviewOrder
            }
        }
    },
    {
        name: 'account.settings.level-move.in-progress',
        url: '/in-progress',
        data: {
            screenName: 'level-move-inprogress',
            subCategory1: 'my-account'
        },
        params: {
            planOverrides: {
                type: ParamType.any,
                raw: true
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveInProgress
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: ['pageData'],
                resolveFn: GetLevelMoveBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveInProgress
            }
        }
    },
    {
        name: 'account.settings.level-move.success',
        url: '/success',
        data: {
            screenName: 'level-move-success',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveSuccess
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData', Transition ],
                resolveFn: GetLevelMoveSuccessBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveSuccess
            }
        }
    },
    {
        name: 'account.settings.level-move.pending',
        url: '/pending',
        data: {
            screenName: 'level-move-pending',
            subCategory1: 'my-account'
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMovePending
            },
            {
                token: BREADCRUMB_TOKEN,
                deps: [ 'pageData' ],
                resolveFn: GetLevelMovePendingBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMovePending
            }
        }
    },
    {
        name: 'account.settings.level-move.failure',
        url: '/failure?errorCode',
        data: {
            screenName: 'level-move-failure',
            subCategory1: 'my-account'
        },
        params: {
            errorCode: {
                type: ParamType.encoded,
                dynamic: true
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveError
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetLevelMoveErrorBreadcrumb
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveError
            }
        }
    },
    {
        name: 'account.settings.level-move.ineligible',
        url: '/ineligible?errorCode',
        data: {
            screenName: 'level-move-ineligible',
            subCategory1: 'my-account'
        },
        params: {
            errorCode: {
                type: ParamType.encoded,
                dynamic: true
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveError
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetLevelMoveErrorBreadcrumb
            },
            {
                token : 'analyticsData',
                deps: [ Analytics, Transition ],
                resolveFn: AddIneligibleMessageToDigitalData
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveError
            }
        }
    },
    {
        name: 'account.settings.level-move.error',
        url: '/error?errorCode',
        data: {
            screenName: 'level-move-error',
            subCategory1: 'my-account'
        },
        params: {
            errorCode: {
                type: ParamType.encoded,
                dynamic: true
            }
        },
        resolve: [
            {
                token: 'customerCheck',
                deps: [ 'accountInfo', StateService, XmStore, Transition ],
                resolveFn: CustomerGuard
            },
            {
                token: 'pageData',
                deps: [ XmStore ],
                resolveFn: GetCmsLevelMoveError
            },
            {
                token: BREADCRUMB_TOKEN,
                resolveFn: GetLevelMoveErrorBreadcrumb
            },
            {
                token : 'analyticsData',
                deps: [ Analytics, Transition ],
                resolveFn: AddErrorMessageToDigitalData
            }
        ],
        views: {
            'content@account': {
                component: XmLevelMoveError
            }
        }
    }
];
